/**
 * User removal — PHASE 2: inventory preview + guarded execute.
 * The muzie.live API recomputes the inventory server-side and refuses on any
 * refusal or hash mismatch, so this UI can never delete from a stale picture.
 * Identity is DEACTIVATED only (hard purge is phase 3); Stripe is untouched
 * (phase 4) — live subscriptions are called out before confirm.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { useApi } from '../../../hooks/useApi';

const ACTION_COLORS = {
  delete: 'error',
  scrub: 'warning',
  redact: 'warning',
  report: 'default',
};

export default function UserRemoval() {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [email, setEmail] = useState('');
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typedEmail, setTypedEmail] = useState('');
  const [executing, setExecuting] = useState(false);
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = async (target) => {
    const e = (target ?? email).trim().toLowerCase();
    if (!e) return;
    setBusy(true);
    setError(null);
    setInv(null);
    setReport(null);
    try {
      const res = await apiRef.current.get(
        `/api/user-removal/inventory?email=${encodeURIComponent(e)}`,
      );
      setInv(res);
    } catch (err) {
      setError(err.message || 'Inventory failed');
    } finally {
      setBusy(false);
    }
  };

  const execute = async () => {
    setExecuting(true);
    setError(null);
    try {
      const res = await apiRef.current.post('/api/user-removal/execute', {
        email: inv.email,
        confirmEmail: typedEmail.trim().toLowerCase(),
        inventoryHash: inv.inventoryHash,
      });
      setConfirmOpen(false);
      setTypedEmail('');
      // Re-run the inventory so the operator sees the post-removal state —
      // load() clears report state, so attach the report after it settles.
      await load(inv.email);
      setReport(res);
    } catch (err) {
      if (err.status === 409) {
        // Refusal or hash mismatch — reload the fresh inventory for re-review.
        // (load() clears the error state, so set the message after it settles.)
        setConfirmOpen(false);
        setTypedEmail('');
        await load(inv.email);
        setError(`Refused: ${err.message} — inventory reloaded, re-review before retrying`);
      } else {
        setError(err.message || 'Removal failed');
      }
    } finally {
      setExecuting(false);
    }
  };

  // Executable when anything removable remains — a profile is NOT required:
  // a partially-removed account (or orphaned data) has no profile but still
  // has rows/objects to clean; the server handles the missing identity.
  const hasRemovableData = (inv?.lines || []).some(
    (l) => ['delete', 'scrub', 'redact'].includes(l.action) && l.count > 0,
  );
  const canExecute =
    inv && (inv.profile || hasRemovableData) && (inv.refusals || []).length === 0 && !report;
  // Export is READ-ONLY — available whenever data exists, refusals or not
  // (portability is owed even to accounts the removal refuses).
  const canExport = inv && hasRemovableData;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PersonRemoveIcon />
        <Typography variant="h5">User Inventory</Typography>
        <Chip size="small" label="inventory · export · removal" color="info" />
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Removal cancels Stripe subscriptions and deletes the customer FIRST
        (a failure there aborts everything else), then deletes muzieRTC + S3 +
        muzie.live data and the AI graph, then hard-PURGES the identity
        account (deactivates with a note if the cloud purge is dark). Export
        the user's data as JSON before removing if they requested portability.
        Cross-tenant and platform rows are shown but never touched.
      </Alert>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="User email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !busy && load()}
        />
        <Button variant="contained" onClick={() => load()} disabled={busy || !email.trim()}>
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Inventory'}
        </Button>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {report && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Alert severity={report.ok ? 'success' : 'warning'} sx={{ mb: 1 }}>
            {report.ok
              ? 'Removal completed cleanly.'
              : 'Removal finished with failures — failed steps are retryable (re-run inventory, then execute again).'}{' '}
            Identity: {report.identity?.note}
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>System</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>Action</TableCell>
                <TableCell align="right">Matched</TableCell>
                <TableCell align="right">Affected</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(report.results || []).map((r, i) => (
                <TableRow key={i} hover sx={r.ok ? undefined : { bgcolor: 'error.light' }}>
                  <TableCell>{r.system}</TableCell>
                  <TableCell>{r.store}</TableCell>
                  <TableCell>{r.action}</TableCell>
                  <TableCell align="right">{r.matched < 0 ? '?' : r.matched}</TableCell>
                  <TableCell align="right">{r.affected}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="error">{r.error || ''}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      {inv && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>{inv.email}</strong> · cleaned id <code>{inv.cleanedId}</code> ·{' '}
              {inv.profile
                ? `profile ${inv.profile.id} (${inv.profile.role ?? 'no role'}${
                    inv.profile.teacherCode ? `, code ${inv.profile.teacherCode}` : ''
                  }${inv.profile.active === false ? ', DEACTIVATED' : ''})`
                : 'no muzie.live account'}
              {' · '}inventory {inv.inventoryHash} @ {new Date(inv.generatedAt).toLocaleString()}
            </Typography>
          </Paper>
          {(inv.refusals || []).map((r, i) => (
            <Alert key={i} severity="warning" sx={{ mb: 1 }}>
              Removal REFUSED: {r}
            </Alert>
          ))}
          <Paper sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>System</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(inv.lines || []).map((l, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{l.system}</TableCell>
                    <TableCell>{l.store}</TableCell>
                    <TableCell>
                      {l.action ? (
                        <Chip size="small" label={l.action} color={ACTION_COLORS[l.action] || 'default'} variant="outlined" />
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{l.count < 0 ? '?' : l.count}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {l.note || ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          {(inv.unswept || []).length > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
              Not swept (visible gap list): {inv.unswept.join(', ')}
            </Typography>
          )}
          {canExecute && (
            <Paper sx={{ p: 2, border: 1, borderColor: 'error.main' }}>
              <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                Danger zone
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<PersonRemoveIcon />}
                  onClick={() => setConfirmOpen(true)}
                >
                  Remove this user…
                </Button>
              </Box>
            </Paper>
          )}
          {canExport && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Data portability
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {[
                  { label: 'Export for user (GDPR)', internal: false },
                  { label: 'Export internal (support)', internal: true },
                ].map(({ label, internal }) => (
                  <Button
                    key={label}
                    variant="outlined"
                    disabled={exporting}
                    onClick={async () => {
                      // GDPR bundle = subject-facing categories; internal =
                      // raw sanitized docs for support/debugging.
                      setExporting(true);
                      setError(null);
                      try {
                        const bundle = await apiRef.current.get(
                          `/api/user-removal/export?email=${encodeURIComponent(inv.email)}${internal ? '&internal=true' : ''}`,
                        );
                        const blob = new Blob([JSON.stringify(bundle, null, 2)], {
                          type: 'application/json',
                        });
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = `muzie-export${internal ? '-internal' : ''}-${inv.email.replace(/[^a-z0-9]/gi, '_')}.json`;
                        a.click();
                        URL.revokeObjectURL(a.href);
                      } catch (err) {
                        setError(err.message || 'Export failed');
                      } finally {
                        setExporting(false);
                      }
                    }}
                  >
                    {exporting ? <CircularProgress size={20} /> : label}
                  </Button>
                ))}
              </Box>
            </Paper>
          )}
        </>
      )}
      <Dialog open={confirmOpen} onClose={() => !executing && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm removal of {inv?.email}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This permanently CANCELS the user&apos;s Stripe subscriptions and deletes their
            Stripe customer, deletes the rows and S3 objects marked <strong>delete</strong> above,
            scrubs the user out of other users&apos; documents, redacts audit rows, and purges
            (or deactivates) the identity account. It cannot be undone.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type the user&apos;s email exactly to confirm:
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={inv?.email}
            value={typedEmail}
            onChange={(e) => setTypedEmail(e.target.value)}
            disabled={executing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={executing}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={executing || typedEmail.trim().toLowerCase() !== (inv?.email || '')}
            onClick={execute}
          >
            {executing ? <CircularProgress size={20} color="inherit" /> : 'Delete everything'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
